/**
 * AuthLayout — Reusable shell for start.inferrex.com pages.
 *
 * Provides: dark radial-gradient background, Inferrex lockup header,
 * contained content area with configurable width, and copyright footer.
 *
 * All styles come from .startshell classes in ./auth-shell.css (design tokens).
 * The consuming app imports that stylesheet via its index.css, alongside
 * tokens.css / tailwind-theme.css.
 */
import type { ReactNode } from "react";

export type ContentSize = "sm" | "md" | "lg" | "xl";

export interface AuthLayoutProps {
  children: ReactNode;
  /** Content max-width: sm=400, md=640, lg=960, xl=1280. Default "sm". */
  size?: ContentSize | undefined;
  /** When true (default), content is vertically centred. */
  centerVertical?: boolean | undefined;
  /** Lockup image src. Defaults to /logos/inferrex-lockup-primary-600.png */
  lockupSrc?: string | undefined;
  /** Link target for the lockup. Defaults to https://inferrex.com */
  lockupHref?: string | undefined;
}

const YEAR = new Date().getFullYear();

export function AuthLayout({
  children,
  size = "sm",
  centerVertical = true,
  lockupSrc = "/logos/inferrex-lockup-primary-600.png",
  lockupHref = "https://inferrex.com",
}: AuthLayoutProps) {
  const shellCls = `startshell${centerVertical ? "" : " startshell--top"}`;
  const contentCls = `startshell__content startshell__content--${size}`;

  return (
    <div className={shellCls}>
      <a href={lockupHref} className="startshell__lockup">
        <img src={lockupSrc} alt="Inferrex" />
      </a>
      <div className={contentCls}>
        {children}
      </div>
      <p className="startshell__footer">
        © {YEAR} Inferrex Ltd · Registered in England & Wales
      </p>
    </div>
  );
}
